import { useId } from "react";
import { Controller, useFormContext, type FieldPath, type FieldValues } from "react-hook-form";

import type { FormRadioGroupProps } from "@luce/ui";
import { Box, Card, cn, Icon, Label, platformProps, RadioIndicator, Typography } from "@luce/ui";

export const FormRadioCheckGroup = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  required = false,
  helperText,
  hideErrorMessage = false,
  errorMessage,
  containerClassName,
  testID,
  options,
  orientation = "horizontal",
  // Kept for API compatibility: the cards' indicators have always rendered at the default size
  size: _size,
  disabled = false,
}: FormRadioGroupProps<TFieldValues, TName>) => {
  const id = useId();
  const { control } = useFormContext<TFieldValues>();

  return (
    <Box
      {...platformProps({ slot: "form-radio-check-group", testID })}
      className={cn("gap-spacing-xxs flex flex-col", containerClassName)}
    >
      {label && (
        // No htmlFor: a group can't be a label's target; the radiogroup is named below
        <Label required={required} disabled={disabled}>
          {label}
        </Label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState }) => {
          const displayError =
            fieldState.error && !hideErrorMessage
              ? (errorMessage ?? fieldState.error.message)
              : undefined;
          return (
            <>
              {/* Each card is itself the radio; the indicator inside is only its visual */}
              <Box
                accessibilityRole="radiogroup"
                accessibilityLabel={label}
                testID={id}
                className={cn(
                  "flex",
                  orientation === "horizontal"
                    ? "gap-spacing-lg flex-row flex-wrap"
                    : "gap-spacing-md flex-col",
                )}
              >
                {options.map((option) => {
                  const checked = field.value === option.value;
                  const optionDisabled = Boolean(disabled || option.disabled);
                  return (
                    <Card
                      key={option.value}
                      direction="row"
                      state={optionDisabled ? "disabled" : checked ? "selected" : "idle"}
                      onPress={() => field.onChange(option.value)}
                      accessibilityRole="radio"
                      checked={checked}
                      className="flex-1"
                    >
                      <Card.Header>
                        <Card.Title>{option.label}</Card.Title>
                      </Card.Header>
                      <Card.Action>
                        <RadioIndicator
                          indicator="check"
                          checked={checked}
                          disabled={optionDisabled}
                        />
                      </Card.Action>
                    </Card>
                  );
                })}
              </Box>
              {(displayError || helperText) && (
                <Box className="gap-spacing-xs flex flex-row items-center">
                  {displayError && (
                    <Icon
                      name="WarningCircle"
                      size={16}
                      weight="fill"
                      className="text-luce-system-danger-default shrink-0"
                    />
                  )}
                  <Typography
                    variant="body-sm"
                    color={
                      displayError ? "system-danger-default" : "neutral-foreground-intermediate"
                    }
                  >
                    {displayError || helperText}
                  </Typography>
                </Box>
              )}
            </>
          );
        }}
      />
    </Box>
  );
};
FormRadioCheckGroup.displayName = "FormRadioCheckGroup";
